// src/components/Dashboard.tsx
import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, where, doc, getDoc, Timestamp, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { BrokerType, Strategy, StrategySubscription } from '../types/trading';
// Import Dialog from headlessui for modal
import { Dialog, Transition } from '@headlessui/react';
import { updateAccountStatus } from '../utils/firebaseHelpers';
import { decrypt, encrypt } from '../utils/encryption';

// Interface for trading account data
interface TradingAccount {
  id: string;
  accountNumber: string;
  accountName: string;
  serverName: string;
  password: string; // Encrypted password
  isConnected: boolean;
  platform: string;
  brokerType?: BrokerType;
  createdAt: any; // Firestore timestamp
  strategies?: Strategy[];
  subscriptions?: StrategySubscription[];
  metaApiAccountId?: string; // Add this field
}

// Add new interface for editing account
interface EditingAccount {
    id: string;
  accountName: string;
  accountNumber: string;
  serverName: string;
  password: string;
}

// Add interface for subscription request
interface SubscriptionRequest {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: any;
  planId?: string;
  planName?: string;
}

// Add interface for subscription request data
interface SubscriptionRequestData {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: any;
  planId?: string;
  planName?: string;
}

// Function to get strategy details by ID
const getStrategyDetails = async (strategyId: string): Promise<Strategy | null> => {
  try {
    if (!strategyId) return null;
    
    const strategyRef = doc(db, 'strategies', strategyId);
    const strategyDoc = await getDoc(strategyRef);
    
    if (strategyDoc.exists()) {
      const data = strategyDoc.data();
      return {
        id: strategyDoc.id,
        name: data.name || 'Unnamed Strategy',
        description: data.description || 'No description available',
        brokerType: data.brokerType,
        minBalance: data.minBalance || 0,
        maxBalance: data.maxBalance || 1000000,
        isActive: data.isActive !== false,
        createdAt: data.createdAt || Timestamp.now(),
        updatedAt: data.updatedAt || Timestamp.now(),
        metaApiId: data.metaApiId // Add metaApiId field
      } as Strategy;
    } else {
      return null;
    }
  } catch (error) {
    console.error(`Error fetching strategy ${strategyId}:`, error);
    return null;
  }
};

// Create a StrategyCard component to handle each subscription display
const StrategyCard: React.FC<{
  subscription: StrategySubscription;
  accountId: string;
  accountName: string;
  strategies: Strategy[];
  onEdit: (subscription: StrategySubscription) => void;
  onUnsubscribe: (subscriptionId: string, accountId: string) => void;
  successMessage?: string;
}> = ({ subscription, accountId, accountName, strategies, onEdit, onUnsubscribe, successMessage }) => {
  const [strategyData, setStrategyData] = useState<Strategy | null>(
    strategies.find(s => s.id === subscription.strategyId) || null
  );
  
  // Add loading state for unsubscribe action
  const [isUnsubscribing, setIsUnsubscribing] = useState<boolean>(false);
  
  useEffect(() => {
    if (!strategyData && subscription.strategyId) {
      const fetchStrategy = async () => {
        const strategy = await getStrategyDetails(subscription.strategyId);
        if (strategy) {
          setStrategyData(strategy);
        }
      };
      fetchStrategy();
    }
  }, [subscription.strategyId, strategyData]);

  // Add handler for unsubscribe with loading state
  const handleUnsubscribe = async () => {
    if (!window.confirm('Are you sure you want to unsubscribe from this strategy?')) {
      return;
    }
    setIsUnsubscribing(true);
    try {
      await onUnsubscribe(subscription.id, accountId);
    } finally {
      setIsUnsubscribing(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="p-4">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div className="flex-grow">
            <div className="flex items-center gap-3 mb-2">
              <h4 className="text-base font-semibold text-gray-900">
                {strategyData?.name || 'Unknown Strategy'}
              </h4>
              <div className="px-2.5 py-1 bg-blue-50 rounded-full">
                <span className="text-xs font-medium text-blue-700">
                  {subscription.tradeVolume} lots
                </span>
              </div>
            </div>
            {strategyData?.description && (
              <p className="text-sm text-gray-600 mt-1.5">
                {strategyData.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit({...subscription, accountId})}
              className="p-2 rounded-lg text-blue-600 hover:text-white hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Edit volume"
              disabled={isUnsubscribing}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
            <button
              onClick={handleUnsubscribe}
              className={`p-2 rounded-lg text-red-600 hover:text-white hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 relative ${isUnsubscribing ? 'cursor-not-allowed opacity-50' : ''}`}
              title="Unsubscribe"
              disabled={isUnsubscribing}
            >
              {isUnsubscribing ? (
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Show success message only for this subscription */}
        {successMessage && (
          <div className="mt-2 p-2 bg-green-50 text-green-700 rounded-md text-sm">
            {successMessage}
          </div>
        )}
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userData, logout } = useAuth();
  const [tradingAccounts, setTradingAccounts] = useState<TradingAccount[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const [selectedAccount, setSelectedAccount] = useState<TradingAccount | null>(null);
  const [availableStrategies, setAvailableStrategies] = useState<Strategy[]>([]);
  const [isLoadingStrategies, setIsLoadingStrategies] = useState<boolean>(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [subscriptionSuccesses, setSubscriptionSuccesses] = useState<{[key: string]: string}>({});
  const [subscriptionRequestStatus, setSubscriptionRequestStatus] = useState<'none' | 'pending' | 'approved' | 'rejected'>('none');
  const isStaging = process.env.REACT_APP_ENVIRONMENT === 'staging';
  
  // State for modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAccount, setModalAccount] = useState<TradingAccount | null>(null);
  const [modalStrategies, setModalStrategies] = useState<Strategy[]>([]);
  const [isLoadingModalStrategies, setIsLoadingModalStrategies] = useState(false);

  // Add state for subscription settings
  const [isSubscriptionSettingsModalOpen, setIsSubscriptionSettingsModalOpen] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null);
  const [tradeVolume, setTradeVolume] = useState(0.01);
  const [subscriptionSettingsError, setSubscriptionSettingsError] = useState<string | null>(null);

  // Add state for editing existing subscriptions
  const [isEditSubscriptionModalOpen, setIsEditSubscriptionModalOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<StrategySubscription | null>(null);
  const [editTradeVolume, setEditTradeVolume] = useState(0.01);
  const [isSubscribing, setIsSubscribing] = useState<boolean>(false);

  // Add state for editing account
  const [editingAccount, setEditingAccount] = useState<EditingAccount | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);

  // Add state for update loading
  const [updatingSubscriptionId, setUpdatingSubscriptionId] = useState<string | null>(null);

  // Helper function to find strategy by ID
  const findStrategyById = (strategyId: string, strategies: Strategy[] = []): Strategy | null => {
    return strategies.find(s => s.id === strategyId) || null;
  };

  // Extract fetchTradingAccounts to be reusable
    const fetchTradingAccounts = async () => {
      if (!currentUser) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Reference to the user's trading accounts collection
        const accountsRef = collection(db, 'users', currentUser.uid, 'tradingAccounts');
        const accountsQuery = query(accountsRef);
        const querySnapshot = await getDocs(accountsQuery);
        
        const accounts: TradingAccount[] = [];
        
      // Get the basic account info
      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        const account: TradingAccount = {
          id: docSnap.id,
          accountNumber: data.accountNumber,
          accountName: data.accountName,
          serverName: data.server,
          password: data.password,
          isConnected: data.isConnected,
          platform: data.platform,
          brokerType: data.brokerType,
          createdAt: data.createdAt,
          strategies: [],
          subscriptions: [],
          metaApiAccountId: data.metaApiAccountId
        };
        
        // Fetch strategies for this broker type directly from Firestore
        if (account.brokerType) {
          const strategiesRef = collection(db, 'strategies');
          // Try both original and uppercase brokerType
          const brokerTypesToTry = [account.brokerType, typeof account.brokerType === 'string' ? account.brokerType.toUpperCase() : account.brokerType];
          let strategies: Strategy[] = [];
          for (const brokerType of brokerTypesToTry) {
            const strategiesQuery = query(
              strategiesRef,
              where('brokerType', '==', brokerType),
              where('isActive', '==', true)
            );
            const strategiesSnapshot = await getDocs(strategiesQuery);
            strategies = strategies.concat(strategiesSnapshot.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                name: data.name || 'Unnamed Strategy',
                description: data.description || 'No description available',
                brokerType: data.brokerType || brokerType,
                minBalance: data.minBalance || 0,
                maxBalance: data.maxBalance || 1000000,
                isActive: data.isActive !== false,
                createdAt: data.createdAt || Timestamp.now(),
                updatedAt: data.updatedAt || Timestamp.now(),
                metaApiId: data.metaApiId // Add metaApiId field
              } as Strategy;
            }));
          }
          // Remove duplicates by id
          const uniqueStrategies: { [id: string]: Strategy } = {};
          strategies.forEach(s => { uniqueStrategies[s.id] = s; });
          strategies = Object.values(uniqueStrategies);
          
          // Get subscriptions for this account from the account's subcollection
          const subscriptionsRef = collection(db, 'users', currentUser.uid, 'tradingAccounts', account.id, 'subscriptions');
          const subscriptionsQuery = query(
            subscriptionsRef,
            where('isActive', '==', true)
          );
          const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
          const subscriptions = subscriptionsSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data
            } as StrategySubscription;
          });
          account.subscriptions = subscriptions;
          // Ensure all strategies referenced by subscriptions are present
          const missingStrategyIds = subscriptions
            .map(s => String(s.strategyId).trim())
            .filter(strategyId => !strategies.some(st => String(st.id).trim() === strategyId));
          for (const strategyId of missingStrategyIds) {
            try {
              const strategyDoc = await getDoc(doc(db, 'strategies', strategyId));
              if (strategyDoc.exists()) {
                const data = strategyDoc.data();
                strategies.push({
                  id: strategyDoc.id,
                  name: data.name || 'Unnamed Strategy',
                  description: data.description || 'No description available',
                  brokerType: data.brokerType || account.brokerType,
                  minBalance: data.minBalance || 0,
                  maxBalance: data.maxBalance || 1000000,
                  isActive: data.isActive !== false,
                  createdAt: data.createdAt || Timestamp.now(),
                  updatedAt: data.updatedAt || Timestamp.now(),
                  metaApiId: data.metaApiId // Add metaApiId field
                } as Strategy);
              }
            } catch (err) {
              console.error('Error fetching missing strategy by ID:', strategyId, err);
            }
          }
          // Remove duplicates again
          const allUniqueStrategies: { [id: string]: Strategy } = {};
          strategies.forEach(s => { allUniqueStrategies[s.id] = s; });
          account.strategies = Object.values(allUniqueStrategies);
        }
        accounts.push(account);
      }
      // Sort accounts by creation date (newest first)
      accounts.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });
      if (accounts.length === 0) {
        setShowHelp(true);
      }
      setTradingAccounts(accounts);
    } catch (err) {
      console.error('Error fetching trading accounts:', err);
      setError('Failed to load your trading accounts. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Use fetchTradingAccounts in the useEffect hook
  useEffect(() => {
    fetchTradingAccounts();
  }, [currentUser]);

  // Update handleSubscribe to handle MetaAPI subscription
  const handleSubscribe = async (accountId: string, strategyId: string, tradeVolume: number) => {
    if (!currentUser) {
      setSubscriptionError('You must be logged in to subscribe to strategies');
      return;
    }

    try {
      console.log('Starting subscription process...', { accountId, strategyId, tradeVolume });
      setSubscriptionError(null);
      setSubscriptionSuccesses({});
      setIsSubscribing(true);

      // Get the account and strategy details
      const account = tradingAccounts.find(acc => acc.id === accountId);
      const strategy = account?.strategies?.find(s => s.id === strategyId);

      if (!account || !strategy) {
        console.error('Account or strategy not found', { account, strategy });
        setSubscriptionError('Account or strategy not found');
        return;
      }

      console.log('Account details:', { 
        accountId: account.id, 
        metaApiAccountId: account.metaApiAccountId,
        accountName: account.accountName 
      });

      // If account has MetaAPI ID, subscribe to MetaAPI strategy
      if (account.metaApiAccountId) {
        // Check if the strategy has a MetaAPI ID
        if (!strategy.metaApiId) {
          console.error('No MetaAPI strategy ID found');
          setSubscriptionError('Strategy is not properly configured. Please contact support.');
          return;
        }

        console.log('Making API call to subscribe to strategy...');
        // Subscribe to MetaAPI strategy
        const apiUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/subscribeToStrategy`;
        console.log('API URL:', apiUrl);
        
        // Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

        try {
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              subscriberId: account.metaApiAccountId,
              strategyId: strategy.metaApiId,
              tradeVolume: tradeVolume,
              subscriberName: account.accountName // Add account name as subscriber name
            }),
            signal: controller.signal
          });

          clearTimeout(timeoutId); // Clear timeout if request completes

          console.log('API Response status:', response.status);
          const responseData = await response.json();
          console.log('API Response data:', responseData);

          if (!response.ok) {
            throw new Error(responseData.message || 'Failed to subscribe to MetaAPI strategy');
          }
        } catch (fetchError: unknown) {
          if (fetchError instanceof Error && fetchError.name === 'AbortError') {
            throw new Error('Request timed out. Please try again.');
          }
          throw fetchError;
        }
      }

      console.log('Creating subscription document in Firestore...');
      // Create a new subscription document in the user's tradingAccounts subcollection
      const subscriptionId = Date.now().toString(); // Simple unique ID
      
      const subscription = {
        id: subscriptionId,
        strategyId,
        tradeVolume,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Get the account document reference first to ensure correct path
      const accountRef = doc(db, 'users', currentUser.uid, 'tradingAccounts', accountId);
      const subscriptionsRef = collection(accountRef, 'subscriptions');
      const newSubscriptionRef = doc(subscriptionsRef, subscriptionId);
      
      // Add the subscription to Firestore
      await setDoc(newSubscriptionRef, subscription);
      
      console.log('Getting strategy details for status update...');
      // Get strategy name for status update
      const strategyRef = doc(db, 'strategies', strategyId);
      const strategySnap = await getDoc(strategyRef);
      const strategyName = strategySnap.exists() ? strategySnap.data().name || strategyId : strategyId;
      
      console.log('Updating account status...');
      // Update account status
      await updateAccountStatus(
        currentUser.uid,
        'strategy_subscribed',
        accountId,
        strategyId,
        `Subscribed to strategy: ${strategyName} with volume ${tradeVolume}`
      );
      
      console.log('Refreshing trading accounts...');
      // Refresh all trading accounts data to ensure we have the latest
      await fetchTradingAccounts();
      
      // Close the subscription settings modal if it's open
      if (isSubscriptionSettingsModalOpen) {
        closeSubscriptionSettings();
      }
      
      setSubscriptionSuccesses({
        [subscriptionId]: `Successfully subscribed to strategy!`
      });
      console.log('Subscription process completed successfully');
    } catch (error) {
      console.error('Error in subscription process:', error);
      setSubscriptionError(error instanceof Error ? error.message : 'Failed to subscribe to strategy. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  // Also update handleUnsubscribe to refresh accounts
  const handleUnsubscribe = async (subscriptionId: string, accountId: string) => {
    if (!window.confirm('Are you sure you want to unsubscribe from this strategy?')) {
      return;
    }

    try {
      setSubscriptionError(null);
      setSubscriptionSuccesses({});
      
      if (!currentUser) {
        setSubscriptionError('You must be logged in to unsubscribe from strategies');
        return;
      }
      
      // Get account and subscription details
      const accountRef = doc(db, 'users', currentUser.uid, 'tradingAccounts', accountId);
      const accountSnap = await getDoc(accountRef);
      const subscriptionRef = doc(db, 'users', currentUser.uid, 'tradingAccounts', accountId, 'subscriptions', subscriptionId);
      const subscriptionSnap = await getDoc(subscriptionRef);
      
      if (!accountSnap.exists() || !subscriptionSnap.exists()) {
        setSubscriptionError('Account or subscription not found');
        return;
      }

      const accountData = accountSnap.data();
      const subscriptionData = subscriptionSnap.data();
      
      // If account has MetaAPI ID, unsubscribe from strategy in MetaAPI
      if (accountData.metaApiAccountId) {
        // Get strategy details to get MetaAPI strategy ID
        const strategyRef = doc(db, 'strategies', subscriptionData.strategyId);
        const strategySnap = await getDoc(strategyRef);
        
        if (strategySnap.exists() && strategySnap.data().metaApiId) {
          // Unsubscribe from strategy in MetaAPI
          const unsubscribeResponse = await fetch(
            `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/unsubscribeFromStrategy`,
            {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                subscriberId: accountData.metaApiAccountId,
                mode: 'close-immediately' // Add mode parameter for DELETE method
              })
            }
          );

          if (!unsubscribeResponse.ok) {
            const errorData = await unsubscribeResponse.json();
            throw new Error(errorData.message || 'Failed to unsubscribe from strategy in MetaAPI');
          }
        }
      }
      
      // Delete the subscription document from Firestore
      await deleteDoc(subscriptionRef);
      
      // Update account status
      await updateAccountStatus(
        currentUser.uid,
        'strategy_unsubscribed',
        accountId,
        subscriptionData.strategyId,
        `Unsubscribed from strategy`
      );
      
      // Refresh all trading accounts data
      await fetchTradingAccounts();
      
      setSubscriptionSuccesses({
        [subscriptionId]: `Successfully unsubscribed from strategy!`
      });
    } catch (err) {
      console.error('Error unsubscribing from strategy:', err);
      setSubscriptionError('Failed to unsubscribe from strategy. Please try again.');
    }
  };

  // Function to open the subscription modal for a specific account
  const openSubscriptionModal = async (account: TradingAccount) => {
    setModalAccount(account);
    setIsModalOpen(true);
    // Reset any previous messages
    setSubscriptionError(null);
    setSubscriptionSuccesses({});
    
    // Load strategies specifically for the modal
    setIsLoadingModalStrategies(true);
    setModalStrategies([]); // Reset strategies
    
    try {
      // Fetch strategies for this broker type directly from Firestore
      if (account.brokerType) {
        const strategiesRef = collection(db, 'strategies');
        
        // First try getting all strategies to see what's available
        const allStrategiesQuery = query(strategiesRef);
        const allStrategiesSnapshot = await getDocs(allStrategiesQuery);
        
        // If no strategies exist, create a sample one
        if (allStrategiesSnapshot.docs.length === 0) {
          const sampleStrategies = await createSampleStrategy(account.brokerType);
          if (sampleStrategies.length > 0) {
            setModalStrategies(sampleStrategies);
            setIsLoadingModalStrategies(false);
            return;
          }
        }
        
        // Try to normalize the broker type (both uppercase and original)
        const normalizedBrokerType = typeof account.brokerType === 'string' ? account.brokerType.toUpperCase() : account.brokerType;
        
        // First try with exact broker type match
        const strategiesQuery = query(
          strategiesRef,
          where('brokerType', '==', account.brokerType),
          where('isActive', '==', true)
        );
        
        const strategiesSnapshot = await getDocs(strategiesQuery);
        
        if (strategiesSnapshot.docs.length === 0 && normalizedBrokerType !== account.brokerType) {
          // If no results and we have a normalized version, try with that
          const normalizedQuery = query(
            strategiesRef,
            where('brokerType', '==', normalizedBrokerType),
            where('isActive', '==', true)
          );
          
          const normalizedSnapshot = await getDocs(normalizedQuery);
          
          // Use the results from normalized query if they exist
          if (normalizedSnapshot.docs.length > 0) {
            const strategies = normalizedSnapshot.docs.map(doc => {
              const data = doc.data();
              // Create a more resilient strategy object with fallbacks for missing data
              return {
                id: doc.id,
                name: data.name || 'Unnamed Strategy',
                description: data.description || 'No description available',
                brokerType: data.brokerType || account.brokerType, // Use account's broker type as fallback
                minBalance: data.minBalance || 0,
                maxBalance: data.maxBalance || 1000000,
                isActive: data.isActive !== false, // Default to active if not specified
                createdAt: data.createdAt || Timestamp.now(),
                updatedAt: data.updatedAt || Timestamp.now(),
                metaApiId: data.metaApiId // Add metaApiId field
              } as Strategy;
            });
            
            setModalStrategies(strategies);
            return;
          }
        }
        
        const strategies = strategiesSnapshot.docs.map(doc => {
          const data = doc.data();
          // Create a more resilient strategy object with fallbacks for missing data
          return {
            id: doc.id,
            name: data.name || 'Unnamed Strategy',
            description: data.description || 'No description available',
            brokerType: data.brokerType || account.brokerType, // Use account's broker type as fallback
            minBalance: data.minBalance || 0,
            maxBalance: data.maxBalance || 1000000,
            isActive: data.isActive !== false, // Default to active if not specified
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
            metaApiId: data.metaApiId // Add metaApiId field
          } as Strategy;
        });
        
        if (strategies.length === 0) {
          // If no strategies are found, provide some examples for testing
          const defaultStrategies = [
            {
              id: 'default-strategy-1',
              name: 'Forex Trend Following',
              description: 'A trend-following strategy for major forex pairs with risk management',
              brokerType: account.brokerType,
              minBalance: 500,
              maxBalance: 50000,
              isActive: true,
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now()
            },
            {
              id: 'default-strategy-2',
              name: 'Gold Breakout',
              description: 'Captures sudden price movements in gold markets based on key resistance levels',
              brokerType: account.brokerType,
              minBalance: 1000,
              maxBalance: 100000,
              isActive: true,
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now()
            }
          ] as Strategy[];
          
          setModalStrategies(defaultStrategies);
        } else {
          setModalStrategies(strategies);
        }
      } else {
        setSubscriptionError('Unable to load strategies: Account broker type is undefined');
      }
    } catch (error) {
      console.error('Error loading strategies for modal:', error);
      setSubscriptionError('Failed to load strategies. Please try again.');
      } finally {
      setIsLoadingModalStrategies(false);
    }
  };

  // Function to close the modal
  const closeModal = () => {
    setIsModalOpen(false);
    setModalAccount(null);
  };

  // Add a helper function to create a sample strategy if needed
  const createSampleStrategy = async (brokerType: any) => {
    try {
      const strategiesRef = collection(db, 'strategies');
      const newStrategyRef = doc(strategiesRef);
      
      const sampleStrategy = {
        id: newStrategyRef.id,
        name: 'Sample Trading Strategy',
        description: 'This is an automatically created sample strategy for testing purposes.',
        brokerType: brokerType,
        minBalance: 100,
        maxBalance: 100000,
        isActive: true,
        adminOnly: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      await setDoc(newStrategyRef, sampleStrategy);
      
      return [sampleStrategy as Strategy];
    } catch (error) {
      console.error('Error creating sample strategy:', error);
      return [];
    }
  };
  
  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Function to open the subscription settings modal
  const openSubscriptionSettings = (strategy: Strategy) => {
    setSelectedStrategy(strategy);
    setTradeVolume(0.01); // Default value
    setSubscriptionSettingsError(null);
    setIsSubscriptionSettingsModalOpen(true);
  };

  // Function to close the subscription settings modal
  const closeSubscriptionSettings = () => {
    setIsSubscriptionSettingsModalOpen(false);
    setSelectedStrategy(null);
  };

  // Toggle help visibility
  const toggleHelp = () => {
    setShowHelp(!showHelp);
  };

  // Add a function to open the edit subscription modal
  const openEditSubscriptionModal = (subscription: StrategySubscription) => {
    setEditingSubscription(subscription);
    setEditTradeVolume(subscription.tradeVolume || 0.01);
    setSubscriptionError(null);
    setIsEditSubscriptionModalOpen(true);
  };

  // Add a function to close the edit subscription modal
  const closeEditSubscriptionModal = () => {
    setIsEditSubscriptionModalOpen(false);
    setEditingSubscription(null);
  };

  // Add function to update subscription volume
  const updateSubscriptionVolume = async (subscriptionId: string, accountId: string, newVolume: number) => {
    try {
      if (!currentUser) {
        return;
      }
      
      if (newVolume <= 0) {
        return;
      }

      setUpdatingSubscriptionId(subscriptionId);
      setSubscriptionError(null);
      setSubscriptionSuccesses({});  // Clear all success messages
      
      // Get the account and subscription references
      const accountRef = doc(db, 'users', currentUser.uid, 'tradingAccounts', accountId);
      const subscriptionRef = doc(db, 'users', currentUser.uid, 'tradingAccounts', accountId, 'subscriptions', subscriptionId);
      
      // Get current subscription data to track changes
      const subscriptionSnap = await getDoc(subscriptionRef);
      if (!subscriptionSnap.exists()) {
        throw new Error('Subscription not found');
      }
      const subscriptionData = subscriptionSnap.data();
      const oldVolume = subscriptionData.tradeVolume;
      const strategyId = subscriptionData.strategyId;

      // Get account data
      const accountSnap = await getDoc(accountRef);
      if (!accountSnap.exists()) {
        throw new Error('Account not found');
      }
      const accountData = accountSnap.data();
      
      // If account has MetaAPI ID, update the subscription in MetaAPI
      if (accountData.metaApiAccountId) {
        // Get strategy details to get MetaAPI strategy ID
        const strategyRef = doc(db, 'strategies', strategyId);
        const strategySnap = await getDoc(strategyRef);
        
        if (strategySnap.exists() && strategySnap.data().metaApiId) {
          // Update subscription in MetaAPI
          const updateResponse = await fetch(
            `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/updateSubscription?subscriberId=${accountData.metaApiAccountId}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                tradeVolume: newVolume
              })
            }
          );

          if (!updateResponse.ok) {
            const errorData = await updateResponse.json();
            throw new Error(errorData.message || 'Failed to update subscription in MetaAPI');
          }
        }
      }
      
      // Update the subscription with the new volume in Firestore
      await setDoc(subscriptionRef, {
        ...subscriptionData,
        tradeVolume: newVolume,
        updatedAt: new Date()
      });
      
      // Update account status
      await updateAccountStatus(
        currentUser.uid,
        'trade_volume_changed',
        accountId,
        strategyId,
        `Changed trade volume from ${oldVolume} to ${newVolume}`
      );
      
      // Refresh trading accounts
      await fetchTradingAccounts();
      
      // Close edit modal
      closeEditSubscriptionModal();
      
      // Update success message only for this specific subscription
      setSubscriptionSuccesses({
        [subscriptionId]: `Trade volume updated successfully for ${accountData.accountName}!`
      });

    } catch (error) {
      console.error('Error updating subscription volume:', error);
      setSubscriptionError('Failed to update trade volume. Please try again.');
    } finally {
      setUpdatingSubscriptionId(null);
    }
  };

  // Add a function to delete a trading account and all its subscriptions
  const handleDeleteTradingAccount = async (accountId: string, accountName: string) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete the trading account "${accountName}"? This action cannot be undone.`);
    
    if (!confirmDelete || !currentUser) return;
    
    try {
      setIsLoading(true);
      
      // Get account details for status update
      const accountRef = doc(db, 'users', currentUser.uid, 'tradingAccounts', accountId);
      const accountSnap = await getDoc(accountRef);
      
      if (accountSnap.exists()) {
        const accountData = accountSnap.data();
        
        // If metaApiAccountId exists, handle MetaAPI cleanup
        if (accountData.metaApiAccountId) {
          try {
            // First, get all subscriptions for this account
            const subscriptionsRef = collection(accountRef, 'subscriptions');
            const subscriptionsSnapshot = await getDocs(subscriptionsRef);
            
            // Unsubscribe from all strategies in MetaAPI
            for (const subscriptionDoc of subscriptionsSnapshot.docs) {
              const subscriptionData = subscriptionDoc.data();
              const strategyRef = doc(db, 'strategies', subscriptionData.strategyId);
              const strategySnap = await getDoc(strategyRef);
              
              if (strategySnap.exists() && strategySnap.data().metaApiId) {
                // Unsubscribe from strategy in MetaAPI
                const unsubscribeResponse = await fetch(
                  `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/unsubscribeFromStrategy`,
                  {
                    method: 'DELETE',
                    headers: {
                      'Content-Type': 'application/json',
                      'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                      subscriberId: accountData.metaApiAccountId,
                      mode: 'close-immediately' // Add mode parameter for DELETE method
                    })
                  }
                );

                if (!unsubscribeResponse.ok) {
                  console.error('Error unsubscribing from strategy:', await unsubscribeResponse.text());
                }
              }
            }

            // Then delete the account from MetaAPI
            const deleteResponse = await fetch(
              `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/deleteAccount?accountId=${accountData.metaApiAccountId}`,
              {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                }
              }
            );

            if (!deleteResponse.ok) {
              const errorData = await deleteResponse.json();
              throw new Error(errorData.message || 'Failed to delete MetaAPI account');
            }
          } catch (error) {
            console.error('Error cleaning up MetaAPI resources:', error);
            // Continue with local deletion even if MetaAPI cleanup fails
          }
        }

        // Get all subscriptions for this account
        const subscriptionsRef = collection(accountRef, 'subscriptions');
        const subscriptionsSnapshot = await getDocs(subscriptionsRef);
        
        // Delete all subscriptions first
        const deletePromises = subscriptionsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
        
        // Then delete the account
        await deleteDoc(accountRef);
        
        // Update account status
        await updateAccountStatus(
          currentUser.uid,
          'deleted_account',
          accountId,
          undefined,
          `Deleted trading account: ${accountName}`
        );
        
        // Remove from local state
        setTradingAccounts(prevAccounts => prevAccounts.filter(account => account.id !== accountId));
      }
    } catch (error) {
      console.error('Error deleting trading account:', error);
      setError('Failed to delete trading account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Add function to handle edit mode
  const handleEditAccount = (account: TradingAccount) => {
    try {
      // Decrypt the password
      const decryptedPassword = decrypt(account.password);
      
      setEditingAccount({
        id: account.id,
        accountName: account.accountName,
        accountNumber: account.accountNumber,
        serverName: account.serverName,
        password: decryptedPassword
      });
      setShowPassword(false);
      setEditError(null);
      setEditSuccess(null);
    } catch (error) {
      console.error('Error decrypting password:', error);
      setEditError('Failed to decrypt password. Please try again.');
    }
  };

  // Add function to cancel edit
  const handleCancelEdit = () => {
    setEditingAccount(null);
    setShowPassword(false);
    setEditError(null);
    setEditSuccess(null);
  };

  // Add function to save account changes
  const handleSaveAccount = async () => {
    if (!editingAccount || !currentUser) return;

    try {
      setEditError(null);
      setEditSuccess(null);

      // Validate required fields
      if (!editingAccount.accountName || !editingAccount.accountNumber || !editingAccount.serverName) {
        setEditError('Please fill in all required fields');
        return;
      }

      // Encrypt the password before saving
      const encryptedPassword = encrypt(editingAccount.password);

      // Update account in Firestore
      const accountRef = doc(db, 'users', currentUser.uid, 'tradingAccounts', editingAccount.id);
      await setDoc(accountRef, {
        accountName: editingAccount.accountName,
        accountNumber: editingAccount.accountNumber,
        server: editingAccount.serverName,
        password: encryptedPassword,
        updatedAt: new Date()
      }, { merge: true });

      // Update account status
      await updateAccountStatus(
        currentUser.uid,
        'account_updated',
        editingAccount.id,
        undefined,
        'Trading account details updated'
      );

      // Refresh trading accounts
      await fetchTradingAccounts();

      // Reset edit state
      setEditingAccount(null);
      setShowPassword(false);
      setEditSuccess('Account details updated successfully!');
    } catch (error) {
      console.error('Error updating account:', error);
      setEditError('Failed to update account details. Please try again.');
    }
  };

  // Add function to check subscription request status
  const checkSubscriptionRequestStatus = async () => {
    if (!currentUser) return;
    
    try {
      const requestsRef = collection(db, 'users', currentUser.uid, 'subscriptionRequests');
      const requestsSnapshot = await getDocs(requestsRef);
      
      if (!requestsSnapshot.empty) {
        const latestRequest = requestsSnapshot.docs
          .map(doc => ({ 
            id: doc.id,
            ...doc.data(),
            status: doc.data().status as 'pending' | 'approved' | 'rejected'
          } as SubscriptionRequestData))
          .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())[0];
        
        setSubscriptionRequestStatus(latestRequest.status || 'pending');
      } else {
        setSubscriptionRequestStatus('none');
      }
    } catch (error) {
      console.error('Error checking subscription request status:', error);
      setSubscriptionRequestStatus('none');
    }
  };

  // Add useEffect to check subscription request status on component mount
  useEffect(() => {
    checkSubscriptionRequestStatus();
  }, [currentUser]);

  if (!currentUser) {
    return <Navigate to="/" />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Navigation Header */}
      <nav className="bg-black shadow">
        {isStaging && (
          <div className="bg-amber-500 text-black text-center py-1 font-medium">
            ⚠️ STAGING ENVIRONMENT - FOR TESTING ONLY ⚠️
          </div>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <img src="/T1Logo180.png" alt="T1 Logo" className="h-8 w-auto" />
                <div className="ml-3 flex items-center">
                  <span className="text-xl font-bold text-white">T1 FX TEAM</span>
                  {isStaging && (
                    <span className="ml-2 bg-amber-500 text-black text-xs px-2 py-0.5 rounded-full font-bold">
                      STAGING
                    </span>
                  )}
                </div>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <Link 
                  to="/dashboard" 
                  className="border-t1blue text-white inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  Dashboard
                </Link>
                <Link 
                  to={userData?.subscription?.status === 'active' ? "/add-trading-account" : "#"}
                  className={`border-transparent ${userData?.subscription?.status === 'active' 
                    ? "text-gray-300 hover:border-gray-300 hover:text-white" 
                    : "text-gray-500 cursor-not-allowed"} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  onClick={(e) => {
                    if (userData?.subscription?.status !== 'active') {
                      e.preventDefault();
                      alert('You need an active subscription to add a trading account. Each subscription allows one trading account with access to multiple automated strategies.');
                    } else if (tradingAccounts.length >= (userData?.subscriptionCount || 1)) {
                      e.preventDefault();
                      alert(`You can only add ${userData?.subscriptionCount || 1} trading account(s) with your current subscriptions. Request additional subscriptions if needed.`);
                    }
                  }}
                  title={userData?.subscription?.status === 'active' 
                    ? (tradingAccounts.length >= (userData?.subscriptionCount || 1)
                        ? `You can only add ${userData?.subscriptionCount || 1} trading account(s) with your current subscriptions` 
                        : "Add a new trading account")
                    : "You need an active subscription to add a trading account"}
                >
                  Add Trading Account
                </Link>
                {userData?.isAdmin && (
                  <Link 
                    to="/admin"
                    className="border-transparent text-gray-300 hover:border-gray-300 hover:text-white inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                  >
                    Admin Panel
                  </Link>
                )}
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={toggleHelp}
                className="mr-4 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue"
                aria-label="Help"
                title="Show help guide"
              >
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Help
                </span>
              </button>
              <button
                onClick={handleSignOut}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-t1blue hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu - only shown on small screens */}
      <div className="sm:hidden bg-black border-t border-gray-700">
        <div className="px-2 py-3 space-y-1">
          <Link 
            to="/dashboard" 
            className="bg-gray-900 border-t1blue text-white block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
          >
            Dashboard
          </Link>
          <Link 
            to={userData?.subscription?.status === 'active' ? "/add-trading-account" : "#"}
            className={`border-transparent ${userData?.subscription?.status === 'active' 
              ? "text-gray-300 hover:bg-gray-900 hover:border-gray-300 hover:text-white" 
              : "text-gray-500 cursor-not-allowed"} block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
            onClick={(e) => {
              if (userData?.subscription?.status !== 'active') {
                e.preventDefault();
                alert('You need an active subscription to add a trading account. Each subscription allows one trading account with access to multiple automated strategies.');
              } else if (tradingAccounts.length >= (userData?.subscriptionCount || 1)) {
                e.preventDefault();
                alert(`You can only add ${userData?.subscriptionCount || 1} trading account(s) with your current subscriptions. Request additional subscriptions if needed.`);
              }
            }}
            title={userData?.subscription?.status === 'active' 
              ? (tradingAccounts.length >= (userData?.subscriptionCount || 1)
                  ? `You can only add ${userData?.subscriptionCount || 1} trading account(s) with your current subscriptions` 
                  : "Add a new trading account")
              : "You need an active subscription to add a trading account"}
          >
            Add Trading Account
          </Link>
          {userData?.isAdmin && (
            <Link 
              to="/admin"
              className="border-transparent text-gray-300 hover:bg-gray-900 hover:border-gray-300 hover:text-white block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            >
              Admin Panel
            </Link>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* User Profile Card */}
        {userData && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Welcome, {userData.firstName}!
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Manage your trading accounts here.
              </p>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Once you have added a trading account, please remember to click on Manage and add your automated strategies.
              </p>
              
              {/* Subscription Status - Modern Design */}
              <div className="mt-6 border-t border-gray-100 pt-4">
                <div className="flex flex-col sm:flex-row">
                  <div className="flex-1">
                    <h4 className="text-base font-medium text-gray-900 mb-2">Subscription Status</h4>
                    
                    {userData.subscription ? (
                      <div className="flex flex-col space-y-2">
                        <div className="flex items-center">
                          <div className={`w-2.5 h-2.5 rounded-full mr-2 ${
                            userData.subscription.status === 'active' ? 'bg-green-500' : 
                            userData.subscription.status === 'expired' ? 'bg-red-500' : 
                            'bg-yellow-500'
                          }`}></div>
                          <span className="font-medium text-gray-700">{userData.subscription.planName}</span>
                          <span className={`ml-2 px-2.5 py-0.5 text-xs font-medium rounded-full ${
                            userData.subscription.status === 'active' ? 'bg-green-100 text-green-800' : 
                            userData.subscription.status === 'expired' ? 'bg-red-100 text-red-800' : 
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {userData.subscription.status}
                          </span>
                          {/* Add subscription count badge */}
                          <span className="ml-2 px-2.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {userData?.subscriptionCount || 1} Active Subscription{(userData?.subscriptionCount || 1) > 1 ? 's' : ''}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">To view and manage your subscription, please visit <a href="https://www.t1fxteam.com" target="_blank" rel="noopener noreferrer">www.t1fxteam.com</a> and go to My Subscriptions</p>
                        
                        {/* Add Request Additional Subscription button */}
                        {userData.subscription?.status === 'active' && (
                          <div className="mt-4 border-t border-gray-100 pt-4">
                            <div className="bg-blue-50 rounded-lg p-4 mb-3">
                              <div className="flex">
                                <div className="flex-shrink-0">
                                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div className="ml-3">
                                  <h3 className="text-sm font-medium text-blue-800">Need Another Trading Account?</h3>
                                  <div className="mt-2 text-sm text-blue-700">
                                    <p>To add another trading account, please follow these steps:</p>
                                    <ol className="list-decimal ml-4 mt-2 space-y-1">
                                      <li>First, purchase a new subscription at <a href="https://www.t1fxteam.com" target="_blank" rel="noopener noreferrer" className="font-medium underline">www.t1fxteam.com</a></li>
                                      <li>Then, click the button below to request activation of your additional subscription</li>
                                      <li>Our admin team will verify your purchase and approve your request</li>
                                    </ol>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Show different content based on request status */}
                            {(subscriptionRequestStatus === 'none' || subscriptionRequestStatus === 'approved') ? (
                              <button
                                onClick={async () => {
                                  if (!currentUser) return;
                                  
                                  if (!window.confirm('Have you already purchased an additional subscription from www.t1fxteam.com? Click OK only if you have completed your purchase.')) {
                                    window.open('https://www.t1fxteam.com', '_blank');
                                    return;
                                  }
                                  
                                  try {
                                    // Create a subscription request document
                                    const requestRef = doc(db, 'users', currentUser.uid, 'subscriptionRequests', Date.now().toString());
                                    await setDoc(requestRef, {
                                      status: 'pending' as const,
                                      createdAt: new Date(),
                                      planId: userData.subscription?.planId,
                                      planName: userData.subscription?.planName
                                    });
                                    
                                    // Update user's account status
                                    await updateAccountStatus(
                                      currentUser.uid,
                                      'subscription_requested',
                                      undefined,
                                      undefined,
                                      'Requested additional subscription'
                                    );
                                    
                                    setSubscriptionRequestStatus('pending');
                                    alert('Additional subscription request submitted successfully! Our admin team will verify your purchase and approve your request.');
                                  } catch (error) {
                                    console.error('Error requesting subscription:', error);
                                    alert('Failed to submit subscription request. Please try again.');
                                  }
                                }}
                                className="mt-2 inline-flex items-center px-3 py-1.5 bg-t1blue text-white text-sm font-medium rounded hover:bg-t1blue-dark transition-colors duration-150"
                              >
                                Request Additional Subscription Activation
                              </button>
                            ) : (
                              <div className={`mt-2 p-3 rounded ${
                                subscriptionRequestStatus === 'pending' ? 'bg-yellow-50 text-yellow-800' :
                                'bg-red-50 text-red-800'
                              }`}>
                                <div className="flex items-center">
                                  {subscriptionRequestStatus === 'pending' && (
                                    <>
                                      <svg className="h-5 w-5 text-yellow-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                      </svg>
                                      <span>Your subscription request is pending approval. We'll notify you once it's processed.</span>
                                    </>
                                  )}
                                  {subscriptionRequestStatus === 'rejected' && (
                                    <>
                                      <svg className="h-5 w-5 text-red-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                      </svg>
                                      <span>Your subscription request was not approved. Please ensure you've purchased a subscription before requesting activation.</span>
                                      <button
                                        onClick={() => setSubscriptionRequestStatus('none')}
                                        className="ml-2 text-red-600 hover:text-red-800 underline text-sm"
                                      >
                                        Try Again
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        <span className="ml-2 text-sm text-gray-600">No active subscription</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 mt-4 sm:mt-0 sm:ml-6 max-w-xs">
                    <div className="bg-gradient-to-r from-t1blue to-blue-500 rounded-lg shadow-sm p-3 text-white">
                      <h4 className="font-medium mb-1 flex items-center text-sm">
                        <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                          <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                        </svg>
                        Subscription Benefits
                      </h4>
                      <ul className="text-xs space-y-0.5 mb-2">
                        <li className="flex items-center">
                          <svg className="h-3 w-3 mr-1 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Add one trading account per subscription
                        </li>
                        <li className="flex items-center">
                          <svg className="h-3 w-3 mr-1 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Subscribe to multiple automated strategies
                        </li>
                        <li className="flex items-center">
                          <svg className="h-3 w-3 mr-1 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Premium support
                        </li>
                      </ul>
                      <a 
                        href="https://www.t1fxteam.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center justify-center w-full px-3 py-1 text-xs font-medium text-t1blue bg-white rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                      >
                        {userData.subscription?.status === 'active' ? 'Manage Subscription' : 'Get Subscription'}
                        <svg className="ml-1 h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Subscription Alert Messages */}
              {!userData.subscription && (
                <div className="mt-4 rounded-md bg-yellow-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">No active subscription</h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>You need an active subscription to add a trading account. Each subscription allows one trading account with access to multiple automated strategies. Please visit <a href="https://www.t1fxteam.com" target="_blank" rel="noopener noreferrer" className="font-medium underline">www.t1fxteam.com</a> to purchase a subscription.</p>
                        <p className="mt-2">If you have already purchased a subscription, please note that it may take up to 8 hours for your subscription to become active on this platform. Thank you for your patience.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Expired Subscription Notice */}
              {userData.subscription?.status === 'expired' && (
                <div className="mt-4 rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Subscription Expired</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>Your subscription has expired. Your trading account will remain in your dashboard but will not be active until you renew. Please visit <a href="https://www.t1fxteam.com" target="_blank" rel="noopener noreferrer" className="font-medium underline">www.t1fxteam.com</a> to renew your subscription.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Help & Getting Started Guide */}
        {showHelp && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Getting Started Guide
              </h3>
              <button 
                className="text-gray-400 hover:text-gray-500" 
                onClick={toggleHelp}
                aria-label="Close help guide"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
              <h4 className="text-md font-bold text-gray-800 mb-4">How to Start Using an Automated Strategy in 3 Simple Steps</h4>
              
              <ol className="list-decimal pl-5 space-y-6">
                <li className="pl-2">
                  <div className="font-medium text-t1blue">Connect Your Trading Account</div>
                  <p className="text-gray-600 mt-1">
                    Click on the "Add Trading Account" button to connect your MetaTrader 5 account. 
                    You'll need your account number, password and server name from your broker.
                  </p>
                  {userData?.subscription?.status === 'active' ? (
                    <Link 
                      to="/add-trading-account"
                      className="mt-2 inline-flex items-center text-sm text-t1blue hover:text-t1blue-light"
                      onClick={(e) => {
                        if (tradingAccounts.length > 0 && process.env.REACT_APP_ENVIRONMENT !== 'staging') {
                          e.preventDefault();
                          alert('You already have a trading account. Each subscription allows only one trading account.');
                        }
                      }}
                    >
                      Add a Trading Account 
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </Link>
                  ) : (
                    <div className="mt-2">
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                              <strong>Important:</strong> You need an active subscription to add trading accounts.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
                
                <li className="pl-2">
                  <div className="font-medium text-t1blue">View Account Details</div>
                  <p className="text-gray-600 mt-1">
                    Once your account is connected, you'll see it appear in your dashboard with its details.
                    Your trading account will display its status, account number, and server information.
                    The account status will be "Pending Deployment" initially while we set up your account.
                  </p>
                </li>
                
                <li className="pl-2">
                  <div className="font-medium text-t1blue">Subscribe to Trading Strategies</div>
                  <p className="text-gray-600 mt-1">
                    From your dashboard, find the "Subscribe to Strategies" button next to your account.
                    Click this button to see available strategies for your broker.
                    Select a strategy and set your trade volume (lots) before confirming your subscription.
                  </p>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-2">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          <strong>Important:</strong> Make sure you have sufficient balance in your account to meet the minimum requirements for the strategy.
                        </p>
                      </div>
                    </div>
                  </div>
                </li>
              </ol>
              
              <div className="mt-6 border-t border-gray-200 pt-4">
                <h4 className="text-md font-bold text-gray-800 mb-2">Managing Your Subscriptions</h4>
                <ul className="list-disc pl-5 text-gray-600 space-y-2">
                  <li>You can view all your active subscriptions directly on your dashboard under each trading account.</li>
                  <li>To unsubscribe from a strategy, click the "Trash" icon next to the strategy.</li>
                  <li>You can adjust the trade volume at any time by clicking the "Edit" icon on your subscription.</li>
                  <li>Each subscription shows lot size information and strategy details.</li>
                </ul>
              </div>

              <div className="mt-6 border-t border-gray-200 pt-4">
                <h4 className="text-md font-bold text-gray-800 mb-2">Managing Your Trading Accounts</h4>
                <ul className="list-disc pl-5 text-gray-600 space-y-2">
                  <li>You can delete a trading account by clicking the trash icon in the top-right corner of the account card.</li>
                  <li>The account status will show "Connected" when your account is actively trading or "Pending Deployment" when it's being set up.</li>
                  <li>Click "Subscribe to Strategies" to view and add available trading strategies to your account.</li>
                  <li>An account without any subscribed strategies will not receive any automated trades.</li>
                  <li>You can add one trading account per active subscription.</li>
                </ul>
              </div>
              
              <div className="mt-6 bg-gray-50 p-4 rounded-md">
                <p className="text-sm text-gray-500">
                  Need help? Contact our support team at 
                  <a href="mailto:support@t1fxteam.co.za" className="text-t1blue hover:text-t1blue-light ml-1"> support@t1fxteam.co.za</a> 
                  {/* or visit our 
                  <a href="https://txfxmobile.com/help" className="text-t1blue hover:text-t1blue-light ml-1" target="_blank" rel="noopener noreferrer">
                    Help Center
                  </a>. */}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Trading Accounts List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Your Trading Accounts
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                All your connected trading accounts appear here.
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleHelp}
                className="text-t1blue hover:text-t1blue-light flex items-center"
                aria-label={showHelp ? "Hide help" : "Show help"}
                title={showHelp ? "Hide help guide" : "Show help guide"}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {showHelp ? "Hide Guide" : "Need Help?"}
              </button>
              {userData?.subscription?.status === 'active' ? (
                <Link 
                  to="/add-trading-account"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-t1blue hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue"
                  onClick={(e) => {
                    if (tradingAccounts.length > 0 && process.env.REACT_APP_ENVIRONMENT !== 'staging') {
                      e.preventDefault();
                      alert('You already have a trading account. Each subscription allows only one trading account.');
                    }
                  }}
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add Trading Account
                </Link>
              ) : (
                <button
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400 cursor-not-allowed"
                  title="You need an active subscription to add a trading account"
                  disabled
                >
                  Add Trading Account
                </button>
              )}
            </div>
          </div>
          
          {isLoading ? (
            <div className="px-4 py-5 sm:p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-t1blue mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading trading accounts...</p>
            </div>
          ) : error ? (
            <div className="px-4 py-5 sm:p-6 text-center">
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
                </div>
              </div>
            </div>
          ) : tradingAccounts.length === 0 ? (
            <div className="border-t border-gray-200 px-6 py-10 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-t1blue-light/30">
                <svg className="h-6 w-6 text-t1blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No trading accounts</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first trading account.
              </p>
              <div className="mt-6">
                {userData?.subscription?.status === 'active' ? (
                  <Link 
                    to="/add-trading-account"
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-t1blue hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue"
                    onClick={(e) => {
                      if (tradingAccounts.length >= (userData?.subscriptionCount || 1)) {
                        e.preventDefault();
                        alert(`You can only add ${userData?.subscriptionCount || 1} trading account(s) with your current subscriptions. Request additional subscriptions if needed.`);
                      }
                    }}
                  >
                    Add Trading Account
                  </Link>
                ) : (
                  <button
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400 cursor-not-allowed"
                    disabled
                  >
                    Add Trading Account
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="space-y-6 p-4">
                {tradingAccounts.map(account => (
                  <div key={account.id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow duration-200">
                    <div className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        {editingAccount?.id === account.id ? (
                          <div className="flex-grow">
                            <input
                              type="text"
                              value={editingAccount.accountName}
                              onChange={(e) => setEditingAccount({...editingAccount, accountName: e.target.value})}
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-t1blue focus:border-t1blue text-base"
                              placeholder="Account Name"
                            />
                        </div>
                        ) : (
                          <div className="flex-grow">
                            <div className="flex items-center gap-3">
                              <h3 className="text-xl font-semibold text-gray-900">{account.accountName}</h3>
                          {account.isConnected ? (
                                <span className="px-2.5 py-1 inline-flex text-xs leading-5 font-medium rounded-full bg-green-100 text-green-800">
                              Connected
                            </span>
                          ) : (
                                <span className="px-2.5 py-1 inline-flex text-xs leading-5 font-medium rounded-full bg-yellow-100 text-yellow-800">
                                  Pending Deployment
                            </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {account.createdAt?.toDate ? 
                                `Added ${account.createdAt.toDate().toLocaleDateString()}` :
                                ''}
                            </p>
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          {editingAccount?.id === account.id ? (
                            <>
                              <button
                                onClick={handleSaveAccount}
                                className="p-2 rounded-lg text-green-600 hover:text-white hover:bg-green-600 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                                title="Save changes"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-500 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
                                title="Cancel editing"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </>
                          ) : (
                            <>
                              {!account.metaApiAccountId && !account.isConnected && (
                                <button
                                  onClick={() => handleEditAccount(account)}
                                  className="p-2 rounded-lg text-blue-600 hover:text-white hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  title="Edit account"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteTradingAccount(account.id, account.accountName)}
                                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-red-500 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
                                title="Delete trading account"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {editingAccount?.id === account.id ? (
                          <>
                            <div className="space-y-4">
                        <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                                <input
                                  type="text"
                                  value={editingAccount.accountNumber}
                                  onChange={(e) => setEditingAccount({...editingAccount, accountNumber: e.target.value})}
                                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-t1blue focus:border-t1blue"
                                  placeholder="Account Number"
                                />
                        </div>
                        <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Server</label>
                                <input
                                  type="text"
                                  value={editingAccount.serverName}
                                  onChange={(e) => setEditingAccount({...editingAccount, serverName: e.target.value})}
                                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-t1blue focus:border-t1blue"
                                  placeholder="Server Name"
                                />
                        </div>
                      </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                              <div className="relative">
                                <input
                                  type={showPassword ? "text" : "password"}
                                  value={editingAccount.password}
                                  onChange={(e) => setEditingAccount({...editingAccount, password: e.target.value})}
                                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-t1blue focus:border-t1blue"
                                  placeholder="Password"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                                >
                                  {showPassword ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                    </svg>
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </div>
                            {editError && (
                              <div className="md:col-span-2">
                                <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{editError}</p>
                              </div>
                            )}
                            {editSuccess && (
                              <div className="md:col-span-2">
                                <p className="text-sm text-green-600 bg-green-50 p-3 rounded-lg">{editSuccess}</p>
                          </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <span className="text-sm font-medium text-gray-500">Account Number</span>
                                  <p className="mt-1 text-base text-gray-900">{account.accountNumber}</p>
                                </div>
                                <div>
                                  <span className="text-sm font-medium text-gray-500">Server</span>
                                  <p className="mt-1 text-base text-gray-900">{account.serverName}</p>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="mt-6 flex justify-end">
                        <button
                          className="inline-flex items-center px-4 py-2.5 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-t1blue hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue transition-colors"
                          onClick={() => openSubscriptionModal(account)}
                        >
                          <svg className="-ml-0.5 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                          Subscribe to Strategies
                        </button>
                      </div>

                      {/* Active Subscriptions */}
                      {account.subscriptions && account.subscriptions.length > 0 ? (
                        <div className="mt-6">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-base font-semibold text-gray-900">Active Strategies</h4>
                            <span className="text-sm text-gray-500">
                              {account.subscriptions.length} {account.subscriptions.length === 1 ? 'strategy' : 'strategies'} active
                            </span>
                          </div>
                          <div className="space-y-3">
                            {account.subscriptions.map(subscription => (
                              <StrategyCard 
                                key={subscription.id}
                                subscription={subscription}
                                accountId={account.id}
                                accountName={account.accountName}
                                strategies={account.strategies || []}
                                onEdit={openEditSubscriptionModal}
                                onUnsubscribe={handleUnsubscribe}
                                successMessage={subscriptionSuccesses[subscription.id]}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-6">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-base font-semibold text-gray-900">Active Strategies</h4>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-6 text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-3">
                              <svg className="h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <p className="text-sm text-gray-500 font-medium">No strategies subscribed</p>
                            <p className="text-xs text-gray-500 mt-1">Your account is currently inactive. Subscribe to at least one strategy to start automated trading.</p>
                          </div>
                        </div>
                      )}

                      {/* Subscription Messages */}
                      {subscriptionError && (
                        <div className="mt-4 rounded-lg bg-red-50 p-4">
                          <p className="text-sm text-red-800">{subscriptionError}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Strategy Subscription Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Subscribe to Trading Strategies
                  </Dialog.Title>
                  
                  {modalAccount && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-4">
                        Select strategies to add to your {modalAccount.accountName} account.
                      </p>
                      
                      {subscriptionError && (
                        <div className="mb-4 p-2 bg-red-50 text-red-700 rounded border border-red-200">
                          {subscriptionError}
                        </div>
                      )}
                      
                      {isLoadingModalStrategies ? (
                        <div className="flex justify-center py-4">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-t1blue"></div>
                        </div>
                      ) : (
                        <div className="mt-4 space-y-3 max-h-60 overflow-y-auto">
                          {modalAccount && modalStrategies
                            .filter(strategy => !modalAccount.subscriptions?.some(s => s.strategyId === strategy.id))
                            .map(strategy => (
                              <div key={strategy.id} className="bg-gray-50 p-3 rounded border border-gray-200">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-900">{strategy.name}</h4>
                                    <p className="text-xs text-gray-500 mt-1">{strategy.description}</p>
                                  </div>
                          <button
                                    onClick={() => openSubscriptionSettings(strategy)}
                                    className="px-3 py-1.5 bg-t1blue text-white text-sm rounded hover:bg-t1blue-dark"
                                  >
                                    Subscribe
                          </button>
                                </div>
                              </div>
                            ))}
                            
                            {modalAccount && modalStrategies.filter(strategy => 
                              !modalAccount.subscriptions?.some(s => s.strategyId === strategy.id)
                            ).length === 0 && (
                              <div className="text-center py-4">
                                <p className="text-sm text-gray-500 italic">You've subscribed to all available strategies.</p>
                              </div>
                        )}
                      </div>
                      )}
                    </div>
                  )}

                  {/* Disclaimer */}
                  <div className="mt-6 border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">Disclaimer:</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      The market observations and insights provided are based on current data and trends and are for informational purposes only. They do not constitute financial advice, recommendations, or endorsements of any kind. Trading and investing involve inherent risks. You should consult with a licensed financial advisor to evaluate the suitability of any strategy, trading, or investment decision based on your individual financial situation. T1 FX Team LLC is not responsible for any losses incurred as a result of using our services.
                    </p>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-t1blue px-4 py-2 text-sm font-medium text-white hover:bg-t1blue-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-t1blue focus-visible:ring-offset-2"
                      onClick={closeModal}
                    >
                      Done
                    </button>
              </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Add the Subscription Settings Modal */}
      <Transition appear show={isSubscriptionSettingsModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeSubscriptionSettings}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Subscription Settings
                  </Dialog.Title>
                  
                  {selectedStrategy && modalAccount && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-4">
                        Set the trade volume for {selectedStrategy.name}
                      </p>
                      
                      {subscriptionSettingsError && (
                        <div className="mb-4 p-2 bg-red-50 text-red-700 rounded border border-red-200">
                          {subscriptionSettingsError}
            </div>
          )}
                      
                      <div className="mt-4">
                        <label htmlFor="tradeVolume" className="block text-sm font-medium text-gray-700">
                          Trade Volume (lots)
                        </label>
                        <div className="mt-1">
                          <input
                            type="number"
                            name="tradeVolume"
                            id="tradeVolume"
                            className="shadow-sm focus:ring-t1blue focus:border-t1blue block w-full sm:text-sm border-gray-300 rounded-md"
                            placeholder="0.01"
                            step="0.01"
                            min="0.01"
                            max="100"
                            value={tradeVolume}
                            onChange={(e) => setTradeVolume(parseFloat(e.target.value))}
                            disabled={isSubscribing}
                          />
        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Recommended volume is 0.01 lots per $100 of your balance
                        </p>
      </div>
                    </div>
                  )}

                  {/* Disclaimer */}
                  <div className="mt-6 border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">Disclaimer:</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      The market observations and insights provided are based on current data and trends and are for informational purposes only. They do not constitute financial advice, recommendations, or endorsements of any kind. Trading and investing involve inherent risks. You should consult with a licensed financial advisor to evaluate the suitability of any strategy, trading, or investment decision based on your individual financial situation. T1 FX Team LLC is not responsible for any losses incurred as a result of using our services.
                    </p>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      onClick={closeSubscriptionSettings}
                      disabled={isSubscribing}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-t1blue px-4 py-2 text-sm font-medium text-white hover:bg-t1blue-dark disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => {
                        if (selectedStrategy && modalAccount) {
                          handleSubscribe(modalAccount.id, selectedStrategy.id, tradeVolume);
                        }
                      }}
                      disabled={isSubscribing}
                    >
                      {isSubscribing ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Subscribing...
                        </>
                      ) : (
                        'Subscribe'
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Add the Edit Subscription Modal */}
      <Transition appear show={isEditSubscriptionModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeEditSubscriptionModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Edit Subscription Settings
                  </Dialog.Title>
                  
                  {editingSubscription && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-4">
                        Update trade volume for this strategy
                      </p>
                      
                      {subscriptionError && (
                        <div className="mb-4 p-2 bg-red-50 text-red-700 rounded border border-red-200">
                          {subscriptionError}
                        </div>
                      )}
                      
                      <div className="mt-4">
                        <label htmlFor="editTradeVolume" className="block text-sm font-medium text-gray-700">
                          Trade Volume (lots)
                        </label>
                        <div className="mt-1">
                          <input
                            type="number"
                            name="editTradeVolume"
                            id="editTradeVolume"
                            className="shadow-sm focus:ring-t1blue focus:border-t1blue block w-full sm:text-sm border-gray-300 rounded-md"
                            placeholder="0.01"
                            step="0.01"
                            min="0.01"
                            max="100"
                            value={editTradeVolume}
                            onChange={(e) => setEditTradeVolume(parseFloat(e.target.value))}
                          />
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Recommended volume is 0.01 lots per $100 of your balance
                        </p>
                        <p className="text-sm text-gray-500 mt-2">Please note that updates to trading strategies may take some time to reflect in your account.</p>
                      </div>
                    </div>
                  )}

                  {/* Disclaimer */}
                  <div className="mt-6 border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">Disclaimer:</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      The market observations and insights provided are based on current data and trends and are for informational purposes only. They do not constitute financial advice, recommendations, or endorsements of any kind. Trading and investing involve inherent risks. You should consult with a licensed financial advisor to evaluate the suitability of any strategy, trading, or investment decision based on your individual financial situation. T1 FX Team LLC is not responsible for any losses incurred as a result of using our services.
                    </p>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      onClick={closeEditSubscriptionModal}
                      disabled={updatingSubscriptionId === editingSubscription?.id}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-t1blue px-4 py-2 text-sm font-medium text-white hover:bg-t1blue-dark disabled:opacity-50"
                      onClick={() => {
                        if (editingSubscription && editingSubscription.accountId) {
                          updateSubscriptionVolume(editingSubscription.id, editingSubscription.accountId, editTradeVolume);
                        }
                      }}
                      disabled={updatingSubscriptionId === editingSubscription?.id}
                    >
                      {updatingSubscriptionId === editingSubscription?.id ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Updating...
                        </>
                      ) : (
                        'Update'
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default Dashboard;